/**
 * Dashboard Editor Route (Dashboards feature)
 *
 * Route: /dashboard/:dashboardId/edit
 *
 * THE HOST. Structurally a clone of the report editor host
 * (apps/web/src/routes/_authenticated/reports/$reportId.edit.tsx): the SAME
 * full-viewport immersive shell — notch header strip, dot-grid recessed canvas,
 * left pages sidebar, right widget palette, zoom controls — built by MOUNTING the
 * report editor's canvas components VERBATIM (CanvasEditor, EditorRightSidebar,
 * FloatingCategoryPicker, FloatingWidgetSettings, ReportDndProvider, EditorToolbar
 * pieces, ZoomControls). Those report files are imported as-is and NOT modified.
 *
 * Dashboard seams (vs the report host):
 *   - a dashboard is a SINGLE FLAT canvas (no pages UI affordance to add many; we
 *     still drive useReportPages with ONE page so the canvas + layers tree work
 *     unchanged). Format is fixed "document".
 *   - load  → useDashboard  (GET /api/dashboards/:id)
 *   - save  → useDashboardSave (debounced PUT /api/dashboards/:id)
 *   - clientId comes from the LOADED dashboard (client-bound) and feeds CanvasEditor
 *     + useReportData for live data, exactly like the report host's effective client.
 *   - breadcrumb → DashboardBreadcrumb (Dashboard > client > dashboard)
 *   - preview/close nav → /dashboard (the hub)
 *   - OMITTED: template loading, format toggle, PDF generate, comparison step.
 *
 * Uses position:fixed z-50 to overlay DashboardLayout for a full-viewport editor.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowLineLeft,
  ArrowLineRight,
  CheckCircle,
  CircleNotch,
  CloudArrowUp,
  ShareNetwork,
} from "@phosphor-icons/react";
import { apiGet } from "../../../lib/apiClient";
import { useReportPages } from "../../../hooks/useReportPages";
import { migrateLayoutToPages } from "@provenleap/types/report-migration";
import {
  zoomAtPoint as computeZoomAtPoint,
  clampPan as computeClampPan,
} from "../../../lib/canvasPanZoom";
import { useDateRange } from "../../../hooks/useDateRange";
import type { DateRangePresetKey } from "../../../lib/dateRangeUtils";
import { useClientIntegrations } from "../../../hooks/useClientIntegrations";
import { unionMemberPlatforms } from "../../../components/reports/rollupPaletteGate";
import { isClientConnected } from "../../../lib/reportClientSource";
import {
  EditorActionsLeft,
  EditorSearchTrigger,
  EditorActionsRight,
  EditorReportTitle,
} from "../../../components/reports/EditorToolbar";
import { DashboardCanvasEditor } from "../../../components/dashboards/DashboardCanvasEditor";
import {
  EditorRightSidebar,
  FloatingCategoryPicker,
  FloatingWidgetSettings,
} from "../../../components/reports/CanvasEditor";
import type { CanvasEditorProps } from "../../../components/reports/CanvasEditor";
import { ReportDndProvider } from "../../../components/reports/ReportDndProvider";
import {
  getNotchMarginRight,
  getPaperLeftShift,
  paperShiftForWidth,
  notchMarginForWidth,
  PICKER_PANEL_WIDTH,
} from "../../../components/reports/categoryPickerRegistry";
import { ZoomControls } from "../../../components/reports/ZoomControls";
import { useCanvasZoom, ZOOM_MIN, ZOOM_MAX } from "../../../hooks/useCanvasZoom";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { WidgetType } from "../../../components/reports/WidgetPalette";
import { nextWidgetTitle } from "../../../components/reports/canvasWidgetDefaults";
import {
  getSmartDefaults,
  planConfiguredWidgetAdd,
  planPresetWidgetsAdd,
} from "../../../lib/gridUtils";
import type { SmartDefaultWidgetType } from "../../../lib/gridUtils";
import type { PresetWidget } from "../../../lib/widgetPresets";
import { PICKER_PLATFORMS, logoUrl } from "../../../lib/integrations";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@provenleap/ui";
import { DashboardBreadcrumb } from "../../../components/dashboards/DashboardBreadcrumb";
import { DashboardShareDialog } from "../../../components/dashboards/DashboardShareDialog";
import { BrandShareDialog } from "../../../components/dashboards/BrandShareDialog";
import { useBrand } from "../../../hooks/useBrands";
import { useDashboard } from "../../../hooks/useDashboard";
import { useDashboardSave } from "../../../hooks/useDashboardSave";
import { fromCanvasLayout, toCanvasLayout } from "../../../lib/dashboardLayout";
import { useToast } from "../../../hooks/useToast";
import { Toast } from "../../../components/clients/Toast";

export const Route = createFileRoute(
  "/_authenticated/dashboard/$dashboardId/edit",
)({
  component: DashboardEditorPage,
});

/** Sidebar width constants — mirror the report host's fit-to-width + zoom offsets. */
const LEFT_SIDEBAR_WIDTH = 220;
const LEFT_SIDEBAR_WIDTH_COLLAPSED = 75;
const RIGHT_SIDEBAR_WIDTH = 165;
const RIGHT_SIDEBAR_WIDTH_COLLAPSED = 75;

/** Fixed format — dashboards are always document-format (no slide deck). */
const DASHBOARD_FORMAT = "document" as const;

/** Minimal client shape from the shared `["clients"]` cache — only the fields the
 *  roll-up member-union needs (Story 40-7, mirrors the report host's ClientForUnion). */
interface ClientForUnion {
  id: string;
  platforms?: string[];
}

/**
 * Unified canvas pan + zoom transform model — identical to the report host's
 * `useCanvasPanZoom` (delegates to the shared, tested canvasPanZoom leaf).
 */
function useCanvasPanZoom() {
  const ref = useRef<HTMLElement | null>(null);
  const [pan, setPanState] = useState({ x: 0, y: 0 });
  const panRef = useRef(pan);
  panRef.current = pan;

  const clampPan = useCallback((next: { x: number; y: number }) => {
    const el = ref.current;
    const wrapper = el?.querySelector(
      "[data-testid='zoom-wrapper']",
    ) as HTMLElement | null;
    if (!el || !wrapper) return next;
    const cRect = el.getBoundingClientRect();
    const wRect = wrapper.getBoundingClientRect();
    return computeClampPan(next, cRect, wRect);
  }, []);

  const setPan = useCallback(
    (next: { x: number; y: number }) => {
      const clamped = clampPan(next);
      panRef.current = clamped;
      setPanState(clamped);
    },
    [clampPan],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const el = ref.current;
      if (!el) return;
      const target = e.target as HTMLElement;
      if (
        target.closest(".react-grid-item") ||
        target.closest(
          "button, a, input, textarea, select, [role='button'], [contenteditable='true']",
        )
      ) {
        return;
      }

      const startX = e.clientX;
      const startY = e.clientY;
      const start = { ...panRef.current };
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";

      const onMove = (ev: MouseEvent) => {
        setPan({
          x: start.x + (ev.clientX - startX),
          y: start.y + (ev.clientY - startY),
        });
      };
      const onUp = () => {
        if (ref.current) {
          ref.current.style.cursor = "";
          ref.current.style.userSelect = "";
        }
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [setPan],
  );

  const zoomAtPoint = useCallback(
    (oldScale: number, newScale: number, clientX: number, clientY: number) => {
      const el = ref.current;
      if (!el) return panRef.current;
      const rect = el.getBoundingClientRect();
      const next = computeZoomAtPoint(
        oldScale,
        newScale,
        { x: clientX, y: clientY },
        { left: rect.left, top: rect.top },
        panRef.current,
      );
      setPan(next);
      return next;
    },
    [setPan],
  );

  return { ref, pan, setPan, onMouseDown, zoomAtPoint };
}

function DashboardEditorPage() {
  const { dashboardId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const rightCollapsed = true; // EditorRightSidebar opens collapsed by default
  const leftWidth = leftCollapsed
    ? LEFT_SIDEBAR_WIDTH_COLLAPSED
    : LEFT_SIDEBAR_WIDTH;

  // Grab-to-pan + zoom-to-cursor (unified transform model).
  const canvasPan = useCanvasPanZoom();

  // Initial framing — a FULL-WIDTH web surface needs no horizontal centering (it
  // already fills the area). Pin it to the top-left with a small top gap so the
  // first widget isn't flush against the notch strip.
  const hasInitialPanRef = useRef(false);
  useEffect(() => {
    if (hasInitialPanRef.current) return;
    const container = canvasPan.ref.current;
    if (!container) return;
    const cRect = container.getBoundingClientRect();
    if (cRect.width === 0) return;
    hasInitialPanRef.current = true;
    canvasPan.setPan({ x: 0, y: 24 });
  });

  // ── Editor state ───────────────────────────────────────────────────────
  const [dashboardName, setDashboardName] = useState("Untitled Dashboard");
  // Persisted publish state — "draft" | "ready" (mirrors the dashboard column).
  // The editor only SEEDS this from the loaded row and threads it back through
  // autosave UNCHANGED; there is no manual publish control. Status auto-flips to
  // "ready" when a dashboard is shared to a client (a future feature), mirroring
  // how a report flips to "ready" on PDF generation.
  const [status, setStatus] = useState<string>("draft");
  const [showExitDialog, setShowExitDialog] = useState(false);
  // Share-link dialog (public dashboard share links) + a local toast for its
  // success/error feedback (the editor has no DashboardLayout toast host).
  const [showShareDialog, setShowShareDialog] = useState(false);
  const { toast, showToast } = useToast();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(
    null,
  );
  const [selectedWidget, setSelectedWidget] = useState<WidgetConfig | null>(null);

  // CanvasEditor selection plumbing (identical refs to the report host).
  const deselectWidgetRef = useRef<() => void>(() => {});
  const selectWidgetRef = useRef<(widgetId: string) => void>(() => {});
  const pendingSelectRef = useRef<string | null>(null);
  const widgetActionsRef = useRef<{
    duplicate: (id: string) => void;
    delete: (id: string) => void;
  }>({ duplicate: () => {}, delete: () => {} });
  const pendingWidgetActionRef = useRef<{
    type: "duplicate" | "delete";
    widgetId: string;
  } | null>(null);

  // The widget editor + category picker share the same right slot — only one open.
  const handleSelectedWidgetChange = useCallback((widget: WidgetConfig | null) => {
    setSelectedWidget(widget);
    if (widget) setActiveCategory(null);
  }, []);
  const handleSetActiveCategory = useCallback((id: string | null) => {
    setActiveCategory(id);
    if (id !== null) deselectWidgetRef.current();
  }, []);

  // Zoom — default 100% on open: the dashboard canvas is a FULL-WIDTH web surface,
  // so it already fills the area at scale 1 (no A4 sheet to shrink-to-fit).
  const canvasZoom = useCanvasZoom({ initialZoomLevel: 100 });
  const rightSidebarWidth = rightCollapsed
    ? RIGHT_SIDEBAR_WIDTH_COLLAPSED
    : RIGHT_SIDEBAR_WIDTH;
  const rightColumnWidth = rightSidebarWidth;

  // Fit-to-width on a FULL-WIDTH web surface = reset to 100% zoom: the dashboard
  // canvas already fills the available width at scale 1 (it is NOT a fixed A4
  // sheet), so "fit" just clears any pan/zoom the user dialed in.
  const computeFitToWidth = useCallback(() => {
    canvasZoom.setZoom(100);
    canvasPan.setPan({ x: 0, y: 0 });
  }, [canvasZoom, canvasPan]);

  // ── Load the saved dashboard ───────────────────────────────────────────
  const {
    data: loadedDashboard,
    isLoading: dashboardLoading,
    isError: dashboardLoadError,
  } = useDashboard(dashboardId);

  // ── Roll-up identity (Story 40-7, AC7) ─────────────────────────────────
  // A dashboard is a ROLL-UP when the loaded row says kind='rollup'. For a
  // roll-up the editor MUST NOT fall through to the single-client populate / mock
  // path — it branches on `isRollup` everywhere a clientId would otherwise drive
  // data. Kind/scope/members are IMMUTABLE post-create (set at /dashboard/new).
  const isRollup = loadedDashboard?.kind === "rollup";

  // CRITICAL (the leak/regression guard — mirrors 40-6's editor): a roll-up has
  // NO effective single client. Forcing `undefined` keeps the single-client
  // client/integration queries + useReportData idle and suppresses the
  // single-client sample-data banner, so a CONNECTED roll-up never renders
  // mock/wrong-client data. A single-client dashboard is byte-identical to today.
  const clientId = isRollup ? undefined : (loadedDashboard?.clientId ?? undefined);

  // The roll-up's resolved member set. The server (40-7 GET) returns the CURRENT
  // members (brand=live active clients, portfolio=junction) on `memberClientIds`.
  const rollupMemberIds = useMemo<string[]>(
    () => (isRollup ? loadedDashboard?.memberClientIds ?? [] : []),
    [isRollup, loadedDashboard?.memberClientIds],
  );

  // ── Brand roll-up sharing (Story 40-8, ADR-RU-3) ───────────────────────
  // A BRAND roll-up (kind='rollup' + rollupScope='brand' + brandId set) is
  // shareable ONLY when its brand carries an owner contact email — the server
  // 400s otherwise. A PORTFOLIO roll-up is NEVER shareable (no Share button at
  // all). We resolve the brand to gate the affordance; the query is idle for
  // non-brand-roll-up dashboards (no brandId → useBrand disabled).
  const brandRollupId =
    isRollup && loadedDashboard?.rollupScope === "brand"
      ? (loadedDashboard?.brandId ?? null)
      : null;
  const { data: shareBrand } = useBrand(brandRollupId ?? undefined);
  const isBrandRollup = brandRollupId !== null;
  const brandHasOwnerEmail =
    typeof shareBrand?.ownerContactEmail === "string" &&
    shareBrand.ownerContactEmail.trim().length > 0;
  const canShareBrandRollup = isBrandRollup && brandHasOwnerEmail;

  // Client info (name + logo) for the header + breadcrumb + live-data label.
  // Idle for a roll-up (clientId is undefined) — no single-client fetch.
  const { data: client } = useQuery<{
    id: string;
    name: string;
    logoUrl: string | null;
    industry: string | null;
    businessGoals: string | null;
    notes: string | null;
  }>({
    queryKey: ["clients", clientId],
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}`);
      if (!res.ok) throw new Error("Failed to load client");
      const json = await res.json();
      return json.data;
    },
    enabled: !!clientId,
  });

  // ── Single-flat-canvas page state ──────────────────────────────────────
  // A dashboard has ONE page. We still drive useReportPages (with a single
  // migrated page) so the canvas + layers tree + undo/redo work UNCHANGED; on
  // change we serialize page[0].widgets back to the canvas blob.
  const initialPages = useMemo(() => migrateLayoutToPages(null), []);
  const {
    pages,
    activePageId,
    activePageWidgets,
    updateActivePageWidgets,
    updateActivePageWidgetsSilent,
    undo,
    redo,
    reset: resetPages,
    version,
  } = useReportPages(initialPages);

  const activePageWidgetsRef = useRef(activePageWidgets);
  activePageWidgetsRef.current = activePageWidgets;

  // Date range (current period — seeded from the loaded dashboard on hydrate).
  const dateRange = useDateRange();

  // ── Hydration: seed the single page from the loaded canvasLayout ONCE ──
  // Ref-guarded on the dashboard id so it fires exactly once per dashboard,
  // never on refetch. `hasHydratedRef` gates autosave so a slow/errored load
  // can never overwrite the real saved row with the blank canvas.
  const hydratedIdRef = useRef<string | null>(null);
  const hasHydratedRef = useRef(false);
  const loadedDashboardId = loadedDashboard?.id;
  useEffect(() => {
    if (!loadedDashboard) return;
    if (loadedDashboard.id === hydratedIdRef.current) return;
    hydratedIdRef.current = loadedDashboard.id;

    const widgets = fromCanvasLayout(loadedDashboard.canvasLayout);
    // Build a single page carrying the saved widgets.
    const hydratedPages = migrateLayoutToPages({ widgets });
    resetPages(hydratedPages);

    if (loadedDashboard.name) setDashboardName(loadedDashboard.name);

    // Seed publish state (text NOT NULL default 'draft' server-side).
    setStatus(loadedDashboard.status ?? "draft");

    // Seed the date range ONCE from the persisted fields. A saved preset
    // re-resolves the LIVE rolling window (so "last 30 days" stays fresh);
    // a custom range is hydrated VERBATIM. With neither, the hook keeps its
    // default (last-30).
    if (
      loadedDashboard.dateRangePreset &&
      loadedDashboard.dateRangePreset !== "custom"
    ) {
      dateRange.selectPreset(loadedDashboard.dateRangePreset as DateRangePresetKey);
    } else if (loadedDashboard.dateRangeStart && loadedDashboard.dateRangeEnd) {
      dateRange.hydrate({
        start: loadedDashboard.dateRangeStart,
        end: loadedDashboard.dateRangeEnd,
      });
    }

    hasHydratedRef.current = true;
    // Keyed on the loaded id ONLY (hydrate-once-per-dashboard); resetPages is
    // stable and `loadedDashboard` is read fresh via the closure.
  }, [loadedDashboardId]);

  // ── Autosave (debounced PUT /api/dashboards/:id) ───────────────────────
  const { saveStatus, isDirty, save } = useDashboardSave({
    dashboardId,
    name: dashboardName,
    widgets: activePageWidgets as WidgetConfig[],
    status,
    dateRangeStart: dateRange.state.start,
    dateRangeEnd: dateRange.state.end,
    dateRangePreset: dateRange.state.presetKey,
    version,
    enabled: hasHydratedRef.current,
  });

  // Keep the cached dashboard in sync so re-entry hydrates the saved canvas +
  // name + publish state + persisted range (mirror what the next load returns).
  // CRITICAL: `canvasLayout` MUST be written here too. With the shared client's
  // 5-minute staleTime, a reopen within that window serves THIS cached row
  // without a refetch; omitting canvasLayout would hand the editor the PRE-EDIT
  // canvas (the widgets would appear lost even though the PUT persisted them).
  useEffect(() => {
    if (saveStatus !== "saved") return;
    queryClient.setQueryData<typeof loadedDashboard>(
      ["dashboard", dashboardId],
      (old) =>
        old
          ? {
              ...old,
              name: dashboardName,
              canvasLayout: toCanvasLayout(
                activePageWidgetsRef.current as WidgetConfig[],
              ),
              status,
              dateRangeStart: dateRange.state.start,
              dateRangeEnd: dateRange.state.end,
              dateRangePreset: dateRange.state.presetKey,
            }
          : old,
    );
    // Only re-sync when a save lands.
  }, [saveStatus]);

  // Clear selection when switching page (single-page, but the canvas plumbing
  // still emits page changes on hydrate/reset) — honoring a pending tree select.
  const prevActivePageIdRef = useRef(activePageId);
  useEffect(() => {
    if (prevActivePageIdRef.current !== activePageId) {
      prevActivePageIdRef.current = activePageId;
      const pending = pendingSelectRef.current;
      pendingSelectRef.current = null;
      const pendingAction = pendingWidgetActionRef.current;
      pendingWidgetActionRef.current = null;
      if (pendingAction) {
        widgetActionsRef.current[pendingAction.type](pendingAction.widgetId);
      } else if (pending) {
        selectWidgetRef.current(pending);
      } else {
        setSelectedWidget(null);
      }
    }
  }, [activePageId]);

  // Connected integrations for this client — drives the canvas live/mock merge
  // and the sample-data banner (same single source as the report host). Idle for
  // a roll-up (clientId === undefined).
  const {
    integrations: clientIntegrations,
    connectedPlatforms: singleClientConnectedPlatforms,
    isLoading: connectedIntegrationsLoading,
  } = useClientIntegrations(clientId);

  // ── Member-union connected platforms for a roll-up (Story 40-7, AC7) ─────
  // The roll-up's connectedPlatforms = the UNION of its members' `platforms`.
  // Read from the shared ["clients"] cache (each Client carries `platforms`) and
  // intersect by member id — no per-member fetch. Idle for a single-client
  // dashboard. Mirrors the 40-6 report editor host.
  const { data: allClientsForUnion } = useQuery<ClientForUnion[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) throw new Error("Failed to load clients");
      const json = await res.json();
      return json.data as ClientForUnion[];
    },
    enabled: isRollup,
  });
  const rollupConnectedPlatforms = useMemo<string[]>(
    () => (isRollup ? unionMemberPlatforms(rollupMemberIds, allClientsForUnion ?? []) : []),
    [isRollup, rollupMemberIds, allClientsForUnion],
  );

  // The effective connected-platform set the canvas + banner read: the member
  // union for a roll-up, the single client's set otherwise.
  const connectedPlatforms = isRollup
    ? rollupConnectedPlatforms
    : singleClientConnectedPlatforms;

  // ── Exit guard (clone of the report host's pattern) ────────────────────
  const handleClose = useCallback(() => {
    if (isDirty) setShowExitDialog(true);
    else navigate({ to: "/dashboard" });
  }, [isDirty, navigate]);

  const handleConfirmExit = useCallback(() => {
    setShowExitDialog(false);
    navigate({ to: "/dashboard" });
  }, [navigate]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      setShowExitDialog(true);
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isDirty]);

  // Cmd/Ctrl+S — immediate save.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [save]);

  // Zoom keyboard shortcuts.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      )
        return;
      if (e.key === "=" || e.key === "+") {
        e.preventDefault();
        canvasZoom.zoomIn();
        return;
      }
      if (e.key === "-") {
        e.preventDefault();
        canvasZoom.zoomOut();
        return;
      }
      if (e.key === "0") {
        e.preventDefault();
        computeFitToWidth();
        return;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canvasZoom, computeFitToWidth]);

  // Trackpad pinch / Ctrl+wheel zoom toward the cursor.
  useEffect(() => {
    const handler = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const oldZoom = canvasZoom.zoomLevel;
      const delta = Math.max(-50, Math.min(50, -e.deltaY));
      const sensitivity = 1.5;
      const newZoom = Math.max(
        ZOOM_MIN,
        Math.min(ZOOM_MAX, oldZoom + delta * sensitivity),
      );
      if (Math.abs(newZoom - oldZoom) < 0.01) return;
      canvasPan.zoomAtPoint(oldZoom / 100, newZoom / 100, e.clientX, e.clientY);
      canvasZoom.setZoom(newZoom);
    };
    window.addEventListener("wheel", handler, { passive: false });
    return () => window.removeEventListener("wheel", handler);
  }, [canvasZoom, canvasPan]);

  // Preview → return to the dashboard hub (no separate preview route for now).
  const handlePreview = useCallback(() => {
    navigate({ to: "/dashboard" });
  }, [navigate]);

  // ── Widget edit / add handlers (identical to the report host) ──────────
  const handleWidgetUpdate = useCallback(
    (widgetId: string, changes: Partial<WidgetConfig>) => {
      updateActivePageWidgets((prev) =>
        prev.map((w) => {
          if (w.id !== widgetId) return w;
          const updated = { ...w, ...changes };
          if (changes.position)
            updated.position = { ...w.position, ...changes.position };
          return updated;
        }),
      );
    },
    [updateActivePageWidgets],
  );

  const handleAddWidget = useCallback(
    (type: WidgetType, initialConfig?: Record<string, unknown>): boolean => {
      const { colSpan, rowSpan } = getSmartDefaults(type as SmartDefaultWidgetType);
      const newWidget = planConfiguredWidgetAdd({
        widgets: activePageWidgetsRef.current,
        type,
        id: crypto.randomUUID(),
        title: nextWidgetTitle(type, activePageWidgetsRef.current),
        colSpan,
        rowSpan,
        config: initialConfig ?? {},
      });
      if (!newWidget) return false;
      updateActivePageWidgets((prev) => [...prev, newWidget]);
      return true;
    },
    [updateActivePageWidgets],
  );

  const handleAddPresetWidgets = useCallback(
    (presetWidgets: PresetWidget[]): boolean => {
      const positioned = planPresetWidgetsAdd({
        widgets: activePageWidgetsRef.current,
        presetWidgets,
      });
      if (!positioned) return false;
      updateActivePageWidgets((prev) => [...prev, ...positioned]);
      return true;
    },
    [updateActivePageWidgets],
  );

  // ── Loading state ──────────────────────────────────────────────────────
  if (dashboardLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-6">
          <div className="w-14 h-14 border-[6px] border-muted border-t-primary rounded-full animate-spin" />
          <p className="text-base font-medium text-muted-foreground animate-pulse">
            Preparing your dashboard editor...
          </p>
        </div>
      </div>
    );
  }

  // ── Load-error state ───────────────────────────────────────────────────
  if (dashboardLoadError) {
    return (
      <div
        data-testid="dashboard-editor-load-error"
        className="fixed inset-0 z-50 flex items-center justify-center bg-background"
      >
        <div className="flex flex-col items-center gap-4 text-center max-w-md px-6">
          <h2 className="text-xl font-semibold text-foreground">
            Couldn’t load this dashboard
          </h2>
          <p className="text-sm text-muted-foreground">
            It may have been deleted, or you may not have access to it. Your
            existing dashboard has not been changed.
          </p>
          <Button
            variant="outline"
            className="rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => navigate({ to: "/dashboard" })}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Right-slot panels (picker / widget settings) share PICKER_PANEL_WIDTH, so the
  // canvas + notch strip shift left exactly as in the report host.
  const widgetEditorOpen = selectedWidget !== null;
  const effectivePaperLeftShift = Math.max(
    getPaperLeftShift(activeCategory),
    widgetEditorOpen ? paperShiftForWidth(PICKER_PANEL_WIDTH) : 0,
  );
  const effectiveNotchMarginRight = Math.max(
    getNotchMarginRight(activeCategory),
    widgetEditorOpen ? notchMarginForWidth(PICKER_PANEL_WIDTH) : 0,
  );

  // The middle breadcrumb crumb: the single client's name, or a roll-up label
  // (Story 40-7) — a roll-up has no single client to name.
  const middleCrumbLabel = isRollup
    ? `Roll-up · ${rollupMemberIds.length} ${rollupMemberIds.length === 1 ? "client" : "clients"}`
    : (client?.name ?? "Client");

  return (
    <>
      {/* Breadcrumb: Dashboard > {client | roll-up} > {dashboard} */}
      <DashboardBreadcrumb
        items={[
          { label: "Dashboard", to: "/dashboard" },
          { label: middleCrumbLabel },
          { label: dashboardName },
        ]}
      />

      <div
        data-testid="dashboard-editor-layout"
        className="fixed inset-0 z-50 flex overflow-hidden bg-background"
      >
        {/* ── Left sidebar: status + (single-page) canvas affordances ─── */}
        <motion.aside
          data-testid="editor-left-sidebar"
          className="shrink-0 flex flex-col bg-background overflow-hidden"
          animate={{ width: leftWidth }}
          initial={false}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {/* Save status — icon stays visible when collapsed */}
          <div
            className="shrink-0 px-4 pt-6 pb-2"
            data-testid="editor-report-status"
          >
            <div
              className="flex items-center justify-center gap-1.5"
              title={
                saveStatus === "unsaved"
                  ? "Unsaved changes"
                  : saveStatus === "saving"
                    ? "Saving…"
                    : "All changes saved"
              }
            >
              {saveStatus === "saving" ? (
                <CircleNotch
                  size={16}
                  weight="bold"
                  className="shrink-0 text-foreground animate-spin"
                />
              ) : saveStatus === "unsaved" ? (
                <CloudArrowUp
                  size={16}
                  weight="fill"
                  className="shrink-0 text-amber-500"
                />
              ) : (
                <CheckCircle
                  size={16}
                  weight="fill"
                  className="shrink-0 text-green-500"
                />
              )}
              <span
                className="text-[13.5px] text-foreground whitespace-nowrap overflow-hidden"
                data-testid="editor-save-status"
                style={{
                  maxWidth: leftCollapsed ? 0 : 200,
                  opacity: leftCollapsed ? 0 : 1,
                  transition: "max-width 0.25s ease, opacity 0.2s ease",
                }}
              >
                {saveStatus === "unsaved"
                  ? "Unsaved changes"
                  : saveStatus === "saving"
                    ? "Saving…"
                    : "All changes saved"}
              </span>
            </div>

            {!leftCollapsed && (
              <div
                className="mt-5 mx-4"
                style={{ borderBottom: "0.5px solid hsl(var(--input) / 0.5)" }}
              />
            )}
          </div>

          {/* Single-canvas hint — a dashboard is one flat canvas (no pages list). */}
          <nav className="flex-1 px-4 overflow-y-auto pt-2">
            {!leftCollapsed && (
              <div className="px-3 pb-2 pt-1 text-[11px] font-semibold tracking-wider text-foreground/70 uppercase">
                Canvas
              </div>
            )}
            <div
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-foreground bg-background border-[0.5px]"
              style={{ borderColor: "hsl(var(--input))" }}
              data-testid="dashboard-canvas-row"
            >
              <span
                className="shrink-0 inline-flex items-center justify-center rounded-md font-semibold tabular-nums"
                style={{
                  width: 22,
                  height: 22,
                  fontSize: 13,
                  color: "hsl(359 100% 71%)",
                }}
              >
                1
              </span>
              {!leftCollapsed && (
                <span className="truncate text-[13.5px]">{dashboardName}</span>
              )}
            </div>
          </nav>

          {/* Footer: Connected Sources + collapse toggle */}
          <div className="shrink-0 px-3 pb-3 space-y-5">
            <button
              type="button"
              onClick={() =>
                clientId &&
                navigate({
                  to: "/clients/$clientId",
                  params: { clientId },
                })
              }
              disabled={!clientId}
              title="Manage connected sources"
              className="w-full px-1 cursor-pointer disabled:cursor-default"
              data-testid="editor-connected-sources"
            >
              {!leftCollapsed && (
                <div className="text-[13.5px] font-medium text-foreground mb-2 text-center">
                  Connected Sources
                </div>
              )}
              {connectedPlatforms.length > 0 ? (
                <div className="flex items-center justify-center flex-wrap gap-2">
                  {connectedPlatforms.map((platformId) => {
                    const platform = PICKER_PLATFORMS.find(
                      (p) => p.platform === platformId,
                    );
                    if (!platform?.domain) return null;
                    return (
                      <img
                        key={platformId}
                        src={logoUrl(platform.domain)}
                        alt={platform.displayName}
                        title={platform.displayName}
                        loading="lazy"
                        className="w-5 h-5 shrink-0 rounded-sm object-contain"
                      />
                    );
                  })}
                </div>
              ) : (
                !leftCollapsed && (
                  <div className="text-[13.5px] italic text-foreground/40 text-center">
                    No sources connected
                  </div>
                )
              )}
            </button>

            <button
              type="button"
              onClick={() => setLeftCollapsed((v) => !v)}
              className="flex items-center justify-center w-full rounded-lg text-foreground bg-accent/50 hover:bg-accent/50 shadow-sm transition-colors cursor-pointer"
              style={{ border: "0.25px solid var(--hairline)" }}
              title={leftCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              data-testid="page-sidebar-collapse"
            >
              <motion.div
                className="flex items-center justify-center py-3 cursor-pointer"
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {leftCollapsed ? (
                  <ArrowLineRight size={18} className="shrink-0 text-foreground" />
                ) : (
                  <ArrowLineLeft size={18} className="shrink-0 text-foreground" />
                )}
                {!leftCollapsed && (
                  <span className="whitespace-nowrap text-[13.5px] font-medium ml-[7px]">
                    Collapse
                  </span>
                )}
              </motion.div>
            </button>
          </div>
        </motion.aside>

        {/* ── Main editor column ───────────────────────────────────── */}
        <div className="relative flex-1 flex flex-col overflow-hidden">
          <ReportDndProvider
            scale={canvasZoom.zoomLevel / 100}
            format={DASHBOARD_FORMAT}
          >
            {/* Notch header strip — shrinks from the right when a panel floats. */}
            <div
              className="relative shrink-0 bg-background h-[21px] overflow-visible"
              style={{
                marginRight: effectiveNotchMarginRight,
                transition: "margin-right 0.25s ease",
              }}
            >
              {/* Left notch — Sources + Schedule (date range) */}
              <div className="absolute z-20 left-6 top-0 h-[48px]">
                <div className="relative h-full flex items-center gap-2 px-3 bg-background rounded-b-[20px]">
                  <EditorActionsRight
                    dateRangeState={dateRange.state}
                    onSelectPreset={dateRange.selectPreset}
                    onSelectCustomRange={dateRange.selectCustomRange}
                    onToggleComparison={dateRange.toggleComparison}
                    onSetComparisonMode={dateRange.setComparisonMode}
                    connectedIntegrations={connectedPlatforms}
                    clientId={clientId}
                  />
                  <div
                    className="absolute inset-0 rounded-b-[20px] pointer-events-none"
                    style={{
                      clipPath: "inset(21px -10px -10px -10px)",
                      border: "0.5px solid var(--hairline)",
                      borderTop: "none",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                    }}
                  />
                </div>
              </div>

              {/* Center notch — the dashboard title */}
              <div className="absolute z-20 left-1/2 -translate-x-1/2 top-0 h-[48px]">
                <div className="relative h-full flex items-center justify-center px-4 bg-background rounded-b-[20px]">
                  <EditorReportTitle
                    reportName={dashboardName}
                    onReportNameChange={setDashboardName}
                  />
                  <div
                    className="absolute inset-0 rounded-b-[20px] pointer-events-none"
                    style={{
                      clipPath: "inset(21px -10px -10px -10px)",
                      border: "0.5px solid var(--hairline)",
                      borderTop: "none",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                    }}
                  />
                </div>
              </div>

              {/* Right notch — Search + Preview/Close */}
              <div className="absolute z-20 right-6 top-0 h-[48px]">
                <div className="relative h-full flex items-center gap-2 px-3 bg-background rounded-b-[20px]">
                  <EditorSearchTrigger />
                  {/* Share affordance. THREE cases (Story 40-8):
                      - single-client dashboard → opens the dashboard share dialog
                        (disabled until the bound client is known on load);
                      - BRAND roll-up → opens the brand roll-up share dialog;
                        disabled (with a tooltip) when the brand has no owner
                        contact email, since the server would 400;
                      - PORTFOLIO roll-up (or a roll-up with no brand) → NO Share
                        button at all (not shareable). */}
                  {isRollup ? (
                    isBrandRollup ? (
                      <button
                        type="button"
                        onClick={() => setShowShareDialog(true)}
                        disabled={!canShareBrandRollup}
                        title={
                          canShareBrandRollup
                            ? "Share brand roll-up"
                            : "Add an owner contact email to this brand to share"
                        }
                        aria-label="Share brand roll-up"
                        data-testid="brand-share-button"
                        className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[13px] font-medium text-foreground bg-background shadow-xs cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-default disabled:hover:translate-y-0"
                        style={{ border: "0.5px solid var(--hairline)" }}
                      >
                        <ShareNetwork
                          size={15}
                          weight="bold"
                          className="shrink-0"
                        />
                        Share
                      </button>
                    ) : null
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowShareDialog(true)}
                      disabled={!clientId}
                      title="Share dashboard"
                      aria-label="Share dashboard"
                      data-testid="dashboard-share-button"
                      className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[13px] font-medium text-foreground bg-background shadow-xs cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-default disabled:hover:translate-y-0"
                      style={{ border: "0.5px solid var(--hairline)" }}
                    >
                      <ShareNetwork size={15} weight="bold" className="shrink-0" />
                      Share
                    </button>
                  )}
                  <EditorActionsLeft
                    onPreview={handlePreview}
                    onClose={handleClose}
                    closeLast
                  />
                  <div
                    className="absolute inset-0 rounded-b-[20px] pointer-events-none"
                    style={{
                      clipPath: "inset(21px -10px -10px -10px)",
                      border: "0.5px solid var(--hairline)",
                      borderTop: "none",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Canvas — dot-grid recessed background, real CanvasEditor inside */}
            <main
              ref={canvasPan.ref as React.Ref<HTMLElement>}
              onMouseDown={canvasPan.onMouseDown}
              data-testid="editor-center-canvas"
              className="relative flex-1 overflow-hidden rounded-tl-[2rem] rounded-tr-[2rem] cursor-grab"
              style={{
                backgroundColor: "var(--app-surface)",
                backgroundImage:
                  "radial-gradient(circle, hsl(214 25% 65% / 0.5) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
                boxShadow: "inset 1px 1px 3px -1px rgba(0,0,0,0.05)",
                borderLeft: "0.5px solid var(--hairline)",
                borderRight: "0.5px solid var(--hairline)",
                borderTop: "0.5px solid var(--hairline)",
              }}
            >
              {/* Sample-data banner — when the bound client has no connected
                  integrations (same connected verdict as the canvas merge). */}
              {clientId &&
                !connectedIntegrationsLoading &&
                !isClientConnected(clientId, connectedPlatforms) && (
                  <div
                    className="mx-auto mb-4 max-w-2xl flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-background shadow-xs"
                    style={{ border: "0.5px solid var(--hairline)" }}
                    data-testid="sample-data-banner"
                  >
                    <span className="text-[13px] text-foreground flex-1">
                      Showing <span className="font-semibold">sample data</span>.
                      Connect integrations to see real numbers for{" "}
                      {client?.name ?? "this client"}.
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        navigate({
                          to: "/clients/$clientId",
                          params: { clientId },
                        })
                      }
                      className="shrink-0 px-3 py-1.5 rounded-2xl text-[13px] font-medium text-white bg-primary cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all"
                    >
                      Connect
                    </button>
                  </div>
                )}
              {/* Roll-up sample banner (Story 40-7) — a roll-up has no single
                  client; it shows sample data when NO member has any connected
                  platform in the union. No "Connect" CTA (members are many). */}
              {isRollup &&
                rollupMemberIds.length > 0 &&
                connectedPlatforms.length === 0 && (
                  <div
                    className="mx-auto mb-4 max-w-2xl flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-background shadow-xs"
                    style={{ border: "0.5px solid var(--hairline)" }}
                    data-testid="rollup-sample-data-banner"
                  >
                    <span className="text-[13px] text-foreground flex-1">
                      Showing <span className="font-semibold">sample data</span>.
                      Connect integrations on the member clients to see aggregated
                      numbers.
                    </span>
                  </div>
                )}
              <DashboardCanvasEditor
                widgets={activePageWidgets as WidgetConfig[]}
                onWidgetsChange={
                  updateActivePageWidgets as CanvasEditorProps["onWidgetsChange"]
                }
                onWidgetsSilentChange={
                  updateActivePageWidgetsSilent as CanvasEditorProps["onWidgetsSilentChange"]
                }
                onUndo={undo}
                onRedo={redo}
                initialWidgets={(pages[0]?.widgets as WidgetConfig[]) ?? []}
                comparisonEnabled={dateRange.state.comparisonEnabled}
                reportDateRangeStart={dateRange.state.start}
                reportDateRangeEnd={dateRange.state.end}
                comparisonStart={dateRange.state.comparisonStart}
                comparisonEnd={dateRange.state.comparisonEnd}
                selectedClientName={client?.name}
                selectedClientLogoUrl={client?.logoUrl ?? undefined}
                clientId={clientId}
                connectedPlatforms={connectedPlatforms}
                // Story 40-7 (AC6/AC7): a roll-up routes live data through
                // useRollupData over the member set + the member-union
                // connectedPlatforms above; the single-client path is unchanged.
                reportKind={isRollup ? "rollup" : "client"}
                memberClientIds={isRollup ? rollupMemberIds : undefined}
                format={DASHBOARD_FORMAT}
                onSelectedWidgetChange={handleSelectedWidgetChange}
                registerDeselect={(fn) => {
                  deselectWidgetRef.current = fn;
                }}
                registerSelect={(fn) => {
                  selectWidgetRef.current = fn;
                }}
                registerWidgetActions={(fns) => {
                  widgetActionsRef.current = fns;
                }}
                zoomLevel={canvasZoom.zoomLevel}
                paperLeftShift={effectivePaperLeftShift}
                panX={canvasPan.pan.x}
                panY={canvasPan.pan.y}
              />
            </main>

            {/* Floating category picker — overlays the canvas dot-grid. */}
            <FloatingCategoryPicker
              activeCategory={activeCategory}
              selectedIntegration={selectedIntegration}
              setSelectedIntegration={setSelectedIntegration}
              clientIntegrations={clientIntegrations}
              onWidgetAdd={handleAddWidget}
              clientId={clientId}
              onPresetWidgetsAdd={handleAddPresetWidgets}
              // Story 40-7 (AC7): the aggregate-only generic-widget panel for a roll-up.
              restrictToRollup={isRollup}
            />
            {/* Widget settings — same right slot as the picker. */}
            <FloatingWidgetSettings
              selectedWidget={selectedWidget}
              onWidgetUpdate={handleWidgetUpdate}
              onDeselectWidget={() => deselectWidgetRef.current()}
              reportDateRangeStart={dateRange.state.start}
              reportDateRangeEnd={dateRange.state.end}
              selectedClientName={client?.name}
              clientId={clientId}
            />
          </ReportDndProvider>
        </div>

        {/* ── Right: widget category icon column ────────────────────── */}
        <aside
          data-testid="editor-right-sidebar"
          className="shrink-0 overflow-y-auto bg-background"
        >
          <EditorRightSidebar
            activeCategory={activeCategory}
            setActiveCategory={handleSetActiveCategory}
            setSelectedIntegration={setSelectedIntegration}
            connectedIntegrations={connectedPlatforms}
            // Story 40-7 (AC7): hide the non-aggregatable category icons for a roll-up.
            restrictToRollup={isRollup}
          />
        </aside>
      </div>

      {/* Unsaved changes exit dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your
              changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExitDialog(false)}>
              Stay
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmExit}
              data-testid="confirm-exit-btn"
            >
              Leave Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Public dashboard share-link dialog — only mountable once the bound
          client is known (the link is client + dashboard scoped). */}
      {clientId && (
        <DashboardShareDialog
          dashboardId={dashboardId}
          clientId={clientId}
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          showToast={showToast}
        />
      )}

      {/* Public brand roll-up share-link dialog (Story 40-8) — only mountable
          for a SHAREABLE brand roll-up (kind=rollup + scope=brand + the brand
          has an owner contact email). Keyed by brandId; the create body carries
          only dashboardId (the server derives brand_id). */}
      {canShareBrandRollup && brandRollupId && (
        <BrandShareDialog
          dashboardId={dashboardId}
          brandId={brandRollupId}
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          showToast={showToast}
        />
      )}

      {/* Local toast host for share-link feedback. */}
      <Toast toast={toast} />

      {/* Zoom controls — fixed, centered on the canvas area */}
      <ZoomControls
        zoomLevel={canvasZoom.displayZoom}
        onZoomIn={canvasZoom.zoomIn}
        onZoomOut={canvasZoom.zoomOut}
        onFitToWidth={computeFitToWidth}
        minZoom={ZOOM_MIN}
        maxZoom={ZOOM_MAX}
        canvasLeftOffset={leftWidth}
        canvasRightOffset={rightColumnWidth}
      />
    </>
  );
}
